<template>
  <div ref="previewContent">
    <div class="column">
      <FormStep :step="1">
        <div class="column full-width q-gutter-y-md q-pb-md">
          <div class="form-step-title">
            {{ sourceTypeTitle }}
          </div>
          <div>
            <div class="step-description q-mb-md">
              {{ sourceTypeDescriptionText }}
            </div>
            <InlinePlayer
              v-if="currentClip"
              auto-fetch-neighbours
              :boundary-end-time="endTime"
              :boundary-start-time="startTime"
              :class="{ radio: !isTvRequest }"
              :clip="currentClip"
              hide-slider
              :poster="playerPoster"
              :start-time="playerCurrentTime"
              :timezone="timezone"
              :type="playerType"
            />
          </div>
        </div>
      </FormStep>
      <FormStep :step="2">
        <div class="column full-width q-gutter-y-md q-pb-md">
          <div class="form-step-title">
            {{ $t("instant_download_request_preview.check_information_title") }}
          </div>
          <div class="row">
            <div class="col-6 column q-gutter-y-md">
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.title_heading") }}
                </div>
                <div>
                  {{ instantDownloadRequest.title }}
                </div>
              </div>
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{
                    $t(
                      "instant_download_request_preview.mention_stream_heading"
                    )
                  }}
                </div>
                <div>
                  {{ streamLabel }}
                </div>
              </div>
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.keyword_heading") }}
                </div>
                <div>
                  {{ keywords }}
                </div>
              </div>
              <div
                v-if="instantDownloadRequest.recipients.length"
                class="column"
              >
                <div class="field-heading q-mb-sm">
                  {{
                    $t(
                      "instant_download_request_preview.additional_recipients_heading"
                    )
                  }}
                </div>
                <div>
                  {{ formattedRecipients }}
                </div>
              </div>
              <div
                v-if="hasSource"
                class="column"
              >
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.channel_heading") }}
                </div>
                <div>{{ source.name }} {{ source.location }}</div>
              </div>
            </div>
            <div class="col-6 column q-gutter-y-md">
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.day_time_heading") }}
                </div>
                <div>
                  {{
                    formatIntlDate(instantDownloadRequest.startTime, {
                      weekday: "long",
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "numeric",
                      timeZone: timezone,
                    })
                  }}
                  {{ shortTimezone(instantDownloadRequest.startTime) }}
                </div>
              </div>
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.duration_heading") }}
                </div>
                <div>
                  {{ clipDuration }}
                </div>
              </div>
              <div
                v-if="hasProgramAiring"
                class="column"
              >
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.program_heading") }}
                </div>
                <div>
                  {{ programAiring.name }}
                </div>
              </div>
              <div class="column">
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.summary_heading") }}
                </div>
                <div>
                  {{ instantDownloadRequest.summary }}
                </div>
              </div>
              <div
                v-if="instantDownloadRequest.thumbnailUrl"
                class="column"
              >
                <div class="field-heading q-mb-sm">
                  {{ $t("instant_download_request_preview.thumbnail_heading") }}
                </div>
                <div>
                  <img
                    :alt="
                      $t('instant_download_request_preview.thumbnail_image_alt')
                    "
                    class="thumbnail-image"
                    :src="instantDownloadRequest.thumbnailUrl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </FormStep>
      <FormStep
        is-last-step
        :step="3"
      >
        <div class="column full-width q-gutter-y-md q-pb-md">
          <div class="form-step-title">
            {{ $t("instant_download_request_preview.check_format_title") }}
          </div>
          <div class="row">
            <div class="q-mb-sm">
              {{ checkFormatHeading }}
            </div>
            <div>
              {{
                $t("instant_download_request_preview.check_format_email_text")
              }}
            </div>
          </div>
        </div>
      </FormStep>
    </div>

    <Portal to="create-instant-download-footer">
      <BaseButton
        :label="$t('global.back')"
        new-design
        @click="onBackClicked"
      />
      <BaseButton
        color="primary"
        :label="$t('instant_download_request_preview.confirm_button')"
        new-design
        @click="onProcessClicked"
      />
    </Portal>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { Portal } from "portal-vue";

import { BaseButton } from "shared/components/base";
import FormStep from "shared/components/FormStep.vue";
import InlinePlayer from "shared/components/players/InlinePlayer.vue";
import {
  durationTimer,
  formatIntlDate,
  getTimezone,
  shortTimezone,
  toSeconds,
} from "shared/helpers/date";
import { useUserStore } from "shared/stores/user";

export default {
  name: "InstantDownloadRequestPreview",
  components: {
    Portal,
    BaseButton,
    InlinePlayer,
    FormStep,
  },
  props: {
    instantDownloadRequest: {
      type: Object,
      required: true,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    programAiring: {
      type: Object,
      default: () => ({}),
    },
    firstClip: {
      type: Object,
      default: null,
    },
    streamLabel: {
      type: String,
      default: "",
    },
    keywords: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "media_item",
    },
    formatsToInclude: {
      type: String,
      default: "media_item",
    },
  },
  emits: ["preview-back-clicked", "preview-process-clicked"],
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      currentClip: this.firstClip,
    };
  },
  computed: {
    playerType() {
      return this.isTvRequest ? "video" : "audio";
    },
    isTvRequest() {
      return this.instantDownloadRequest.sourceType === "TvChannel";
    },
    sourceTypeTitle() {
      if (this.type === "media_item") {
        return this.$t(
          `instant_download_request_preview.media_item_source_title`
        );
      }

      return this.$t(
        `instant_download_request_preview.transcript_request_source_title`
      );
    },
    sourceTypeDescriptionText() {
      if (this.type === "media_item") {
        if (this.playerType === "video") {
          return this.$t(
            `instant_download_request_preview.media_item_video_source_description`
          );
        }

        return this.$t(
          `instant_download_request_preview.media_item_audio_source_description`
        );
      }

      if (this.playerType === "video") {
        return this.$t(
          `instant_download_request_preview.transcript_request_video_source_description`
        );
      }

      return this.$t(
        `instant_download_request_preview.transcript_request_audio_source_description`
      );
    },
    playerPoster() {
      if (this.isTvRequest) return this.instantDownloadRequest.thumbnailUrl;

      if (this.hasSource) return this.source.logo_url;

      return "none";
    },
    playerCurrentTime() {
      return this.instantDownloadRequest.startTime;
    },
    formattedRecipients() {
      const recipients = this.instantDownloadRequest.recipients || [];

      return recipients.join(", ");
    },
    hasSource() {
      return this.source && Object.keys(this.source).length;
    },
    hasProgramAiring() {
      return this.programAiring && Object.keys(this.programAiring).length;
    },
    timezone() {
      return this.source ? this.source.time_zone : getTimezone();
    },
    startTime() {
      return toSeconds(this.instantDownloadRequest.startTime);
    },
    endTime() {
      return toSeconds(this.instantDownloadRequest.endTime);
    },
    clipDuration() {
      return durationTimer(
        new Date(this.instantDownloadRequest.endTime),
        new Date(this.instantDownloadRequest.startTime)
      );
    },
    checkFormatHeading() {
      if (this.type === "transcript_request") {
        return this.$t(
          "instant_download_request_preview.check_format_transcript_heading"
        );
      }

      if (this.formatsToInclude === "both") {
        if (this.isTvRequest) {
          return this.$t(
            "instant_download_request_preview.check_format_both_video_heading"
          );
        }

        return this.$t(
          "instant_download_request_preview.check_format_both_audio_heading"
        );
      }

      if (this.isTvRequest) {
        return this.$t(
          "instant_download_request_preview.check_format_video_media_item_heading"
        );
      }

      return this.$t(
        "instant_download_request_preview.check_format_audio_media_item_heading"
      );
    },
    featureType() {
      if (this.type === "transcript_request") {
        return "has_instant_edit_transcript";
      }

      return "has_instant_edit_media_item";
    },
    reachedContractAllowance() {
      return this.$features.reachedContractAllowance(this.featureType);
    },
  },
  mounted() {
    const { previewContent } = this.$refs;
    previewContent.scrollIntoView();
  },
  methods: {
    formatIntlDate,
    shortTimezone(isoDateString) {
      const parsedDate = new Date(isoDateString);

      return shortTimezone(parsedDate, this.source.time_zone);
    },
    onBackClicked() {
      this.$emit("preview-back-clicked");
    },
    onProcessClicked() {
      if (this.reachedContractAllowance) {
        this.$modals.open("CreateInstantDownloadProcessModal", {
          props: {
            type: this.type,
          },
          events: {
            confirm: () => {
              this.$emit("preview-process-clicked");
            },
          },
        });
      } else {
        this.$emit("preview-process-clicked");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.step-description {
  font-size: 12px;
  line-height: 150%;
}

.thumbnail-image {
  width: 180px;
  height: auto;
  border-radius: 5px;
}

.form-step-title {
  font-size: 16px;
  height: 30px;
  display: flex;
  align-items: center;
}
</style>
